import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material/styles';
import { Box, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import { getGridStringOperators } from '@mui/x-data-grid';
import moment from 'moment';
import ListView from '../../components/views/listView';
import FieldText from '../../components/general/fields/fieldText';
import FieldSelect from '../../components/general/fields/fieldSelect';
import { HOME } from '../../../routes/paths';
import { MasterMessage } from '../../../masters/masterMessages';
import ModalIvrTypificationBase from './modalIvrTypificationBase';
import { SwalCustomClass } from '../../components/general/toastSwal/swalCustomClass';
import Swal from 'sweetalert2';
import {
    asyncGetIvrTypificationBase,
    asyncGetTemplateIvrTypificationBase,
    asyncPatchIvrTypificationBaseActive,
    asyncSyncIvrTypificationBaseToCrmware,
    IvrTypificationActiveHttpError,
    IvrTypificationSyncHttpError,
} from '../../../services/ivrTypificationBase.service';
import { IvrTypificationTemplateTypeEnum } from '../../../shared/types/ivrTypificationBase';
import { TYPIFICATION_TYPE_OPTIONS } from './constants';
import { startIvrTypificationBaseTour } from './driver';

const sanitizeTemplateFilenameSegment = (value: string) =>
    value
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9._-]/g, '');

/** Descarga el archivo con nombre que incluye el formato; si fetch falla (p. ej. CORS), abre la URL. */
const downloadTemplateWithFormatName = async (
    url: string,
    formatDisplayName: string
): Promise<void> => {
    const urlPath = url.split('?')[0];
    const extMatch = /\.([a-zA-Z0-9]+)$/.exec(urlPath);
    const ext = extMatch ? extMatch[1].toLowerCase() : 'xlsx';
    const safe =
        sanitizeTemplateFilenameSegment(formatDisplayName) || 'formato';
    const filename = `plantilla_${safe}.${ext}`;

    try {
        const res = await fetch(url);
        if (!res.ok) {
            window.open(url);
            return;
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
    } catch {
        window.open(url);
    }
};

const ListIvrTypificationBase = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<any>([]);
    const [lengthRows, setLengthRows] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [exportingTemplate, setExportingTemplate] = useState<boolean>(false);
    const [syncingCrmware, setSyncingCrmware] = useState<boolean>(false);
    const [activeUpdatingId, setActiveUpdatingId] = useState<string | null>(null);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(20);
    const [open, setOpen] = useState<boolean>(false);
    const [saveLoad, setSaveLoad] = useState<boolean>(false);
    const [initialProcessName, setInitialProcessName] = useState('');
    const [processCode, setProcessCode] = useState('');
    const [typificationType, setTypificationType] = useState('');
    const [typificationCode, setTypificationCode] = useState('');
    const [initialTypification, setInitialTypification] = useState('');
    const [subTypificationCode, setSubTypificationCode] = useState('');
    const [initialSubTypificationName, setInitialSubTypificationName] =
        useState('');
    const [pqrstType, setPqrstType] = useState('');
    const [customerType, setCustomerType] = useState('');
    const [responseTime, setResponseTime] = useState('');
    const [active, setActive] = useState('');
    const filterOperators = getGridStringOperators().filter(({ value }) =>
        ['equals'].includes(value)
    );

    const {
        titleList,
        messageOkTemplate,
        messageErrorTemplate,
        helpTourButton,
        syncButton,
        syncConfirmTitle,
        syncConfirmMessage,
        syncSuccessMessage,
        syncErrorMessage,
    } = MasterMessage.ivrTypificationBase;

    useEffect(() => {
        if (saveLoad && page !== 0) {
            setPage(0);
            setSaveLoad(false);
            return;
        } else if (saveLoad) {
            setSaveLoad(false);
        }

        if (!loading && !saveLoad) return;

        asyncGetIvrTypificationBase({
            page,
            limit: pageSize,
            initialProcessName,
            processCode,
            typificationType: typificationType as any,
            typificationCode,
            initialTypification,
            subTypificationCode,
            initialSubTypificationName,
            pqrstType,
            customerType,
            responseTime,
            active,
        })
            .then((body: any) => {
                if (body?.items !== undefined) {
                    setRows(body.items.items || []);
                    setLengthRows(body.items.length || 0);
                }
            })
            .finally(() => setLoading(false));
    }, [
        page,
        pageSize,
        saveLoad,
        initialProcessName,
        processCode,
        typificationType,
        typificationCode,
        initialTypification,
        subTypificationCode,
        initialSubTypificationName,
        pqrstType,
        customerType,
        responseTime,
        active,
        loading,
    ]);

    const paginator = (paginationModel: any) => {
        if (paginationModel.page !== page) {
            setPage(paginationModel.page);
            setLoading(true);
        } else if (paginationModel.pageSize !== pageSize) {
            setPageSize(paginationModel.pageSize);
            setLoading(true);
        }
    };

    const columns = [

        {
            field: 'actions',
            headerName: 'Acciones',
            minWidth: 72,
            maxWidth: 96,
            flex: 0.3,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box
                    id="ivr-tb-actions-header"
                    component="span"
                    sx={{
                        width: '100%',
                        display: 'inline-block',
                        textAlign: 'center',
                        fontWeight: 600,
                    }}
                >
                    Acciones
                </Box>
            ),
            renderCell: (params: any) => {
                const row = params?.row || {};
                const id = row?.id;
                const isActive = Boolean(row?.active);
                const isUpdating = activeUpdatingId
                    ? String(activeUpdatingId) === String(id)
                    : false;

                const nextActive = !isActive;
                const actionTitle = nextActive ? 'Activar' : 'Desactivar';

                return (
                    <Tooltip title={actionTitle} placement="top">
                        <span>
                            <IconButton
                                size="small"
                                disabled={!id || isUpdating}
                                aria-label={actionTitle}
                                onClick={() =>
                                    handleToggleActive(String(id), nextActive)
                                }
                                sx={(theme) => {
                                    const main = nextActive
                                        ? theme.palette.success.main
                                        : theme.palette.error.main;
                                    const dark = nextActive
                                        ? theme.palette.success.dark
                                        : theme.palette.error.dark;
                                    return {
                                        border: '1px solid',
                                        borderColor: main,
                                        color: main,
                                        backgroundColor: alpha(main, 0.1),
                                        '&:hover': {
                                            backgroundColor: alpha(main, 0.22),
                                            borderColor: dark,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: main,
                                        },
                                    };
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress
                                        size={20}
                                        sx={(theme) => ({
                                            color: nextActive
                                                ? theme.palette.success.main
                                                : theme.palette.error.main,
                                        })}
                                    />
                                ) : nextActive ? (
                                    <CheckIcon
                                        fontSize="small"
                                        sx={(theme) => ({
                                            color: theme.palette.success.main,
                                        })}
                                    />
                                ) : (
                                    <CancelIcon
                                        fontSize="small"
                                        sx={(theme) => ({
                                            color: theme.palette.error.main,
                                        })}
                                    />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 110,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            filterOperators,
            renderCell: (row: any) =>
                row.row.active ? (
                    <Chip label="SI" sx={{ ml: 1, fontSize: 11.5 }} color="success" />
                ) : (
                    <Chip label="NO" sx={{ ml: 1, fontSize: 11.5 }} color="error" />
                ),
        },
        {
            field: 'loadIdentifier',
            headerName: 'Identificador carga',
            minWidth: 180,
            flex: 1,
            filterOperators,
        },
        {
            field: 'typificationType',
            headerName: 'Tipo tipificacion',
            minWidth: 170,
            flex: 1,
            filterOperators,
        },
        {
            field: 'initialProcessName',
            headerName: 'Proceso inicial',
            minWidth: 170,
            flex: 1,
            filterOperators,
        },
        {
            field: 'processCode',
            headerName: 'Codigo proceso',
            minWidth: 140,
            flex: 1,
            filterOperators,
        },
        {
            field: 'typificationCode',
            headerName: 'Codigo tipificacion',
            minWidth: 140,
            flex: 1,
            filterOperators,
        },
        {
            field: 'initialTypification',
            headerName: 'Tipificacion inicial',
            minWidth: 170,
            flex: 1,
            filterOperators,
        },
        {
            field: 'subTypificationCode',
            headerName: 'Codigo subtipificacion',
            minWidth: 160,
            flex: 1,
            filterOperators,
        },
        {
            field: 'initialSubTypificationName',
            headerName: 'Subtipificacion inicial',
            minWidth: 180,
            flex: 1,
            filterOperators,
        },
        {
            field: 'childSubTypificationCode',
            headerName: 'Codigo child subtipificacion',
            minWidth: 190,
            flex: 1,
            filterOperators,
        },
        {
            field: 'childSubTypificationName',
            headerName: 'Child subtipificacion',
            minWidth: 180,
            flex: 1,
            filterOperators,
        },
        {
            field: 'pqrstType',
            headerName: 'Tipo PQRST',
            minWidth: 130,
            flex: 1,
            filterOperators,
        },
        {
            field: 'customerType',
            headerName: 'Tipo cliente',
            minWidth: 130,
            flex: 1,
            filterOperators,
        },
        {
            field: 'responseTime',
            headerName: 'Tiempo respuesta',
            minWidth: 130,
            flex: 1,
            filterOperators,
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 140,
            flex: 1,
            filterable: false,
            valueFormatter: (params: any) =>
                params?.value
                    ? `${params.value.name || ''} ${params.value.surname || ''}`.trim()
                    : '',
        },
        {
            field: 'created_at',
            headerName: 'Fecha creacion',
            minWidth: 150,
            flex: 1,
            type: 'date',
            filterable: false,
            valueFormatter: (params: any) =>
                params?.value ? moment(params.value).format('DD/MM/YYYY HH:mm') : '',
        },
    ];

    const clearFilters = () => {
        setInitialProcessName('');
        setProcessCode('');
        setTypificationType('');
        setTypificationCode('');
        setInitialTypification('');
        setSubTypificationCode('');
        setInitialSubTypificationName('');
        setPqrstType('');
        setCustomerType('');
        setResponseTime('');
        setActive('');
        setLoading(true);
    };

    const handleToggleActive = async (id: string, nextActive: boolean) => {
        const actionText = nextActive ? 'activar' : 'desactivar';

        // eslint-disable-next-line no-alert
        if (
            !window.confirm(
                `¿Está seguro que desea ${actionText} este registro?`
            )
        ) {
            return;
        }

        setActiveUpdatingId(id);

        try {
            await asyncPatchIvrTypificationBaseActive(id, nextActive);

            setRows((prev: any[]) =>
                (prev || []).map((r) =>
                    String(r?.id) === String(id)
                        ? { ...r, active: nextActive }
                        : r
                )
            );

            SwalCustomClass.fire({
                text: nextActive
                    ? 'Se activó el registro exitosamente'
                    : 'Se desactivó el registro exitosamente',
                icon: 'success',
                iconColor: '#1d1d1d',
            });
        } catch (err: unknown) {
            const httpErr = err as IvrTypificationActiveHttpError;
            const message =
                typeof httpErr?.body === 'object' && httpErr?.body != null
                    ? (httpErr.body as any)?.message
                    : undefined;

            SwalCustomClass.fire({
                text:
                    message ||
                    'Se presentó un error al actualizar el estado del registro',
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } finally {
            setActiveUpdatingId(null);
        }
    };

    const validateFilters = (filter: any) => {
        if (!filter) {
            setInitialProcessName('');
            setProcessCode('');
            setTypificationType('');
            setTypificationCode('');
            setInitialTypification('');
            setSubTypificationCode('');
            setInitialSubTypificationName('');
            setPqrstType('');
            setCustomerType('');
            setResponseTime('');
            setActive('');
            setLoading(true);
            return;
        }

        switch (filter.field) {
            case 'loadIdentifier':
                break;
            case 'initialProcessName':
                setInitialProcessName(filter.value || '');
                break;
            case 'processCode':
                setProcessCode(filter.value || '');
                break;
            case 'typificationType':
                setTypificationType(filter.value || '');
                break;
            case 'typificationCode':
                setTypificationCode(filter.value || '');
                break;
            case 'initialTypification':
                setInitialTypification(filter.value || '');
                break;
            case 'subTypificationCode':
                setSubTypificationCode(filter.value || '');
                break;
            case 'initialSubTypificationName':
                setInitialSubTypificationName(filter.value || '');
                break;
            case 'pqrstType':
                setPqrstType(filter.value || '');
                break;
            case 'customerType':
                setCustomerType(filter.value || '');
                break;
            case 'responseTime':
                setResponseTime(filter.value || '');
                break;
            case 'active':
                setActive(String(filter.value));
                break;
        }
        setLoading(true);
    };

    const handleDownloadTemplate = async () => {
        try {
            const optionHtml = TYPIFICATION_TYPE_OPTIONS.map(
                (o) =>
                    `<option value="${o.id}">${o.name}</option>`
            ).join('');

            const modalResult = await Swal.fire({
                title: 'Selecciona el tipo de tipificación',
                html: `
                    <div style="display:flex;flex-direction:column;gap:10px;text-align:left;">
                        <label for="swal-typification-type"><b>Tipo tipificación</b></label>
                        <select id="swal-typification-type" class="swal2-select" style="width:100%;margin:0;">
                            <option value="">Selecciona una opción</option>
                            ${optionHtml}
                        </select>
                    </div>
                `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Exportar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#1d1d1d',
                preConfirm: () => {
                    const typificationTypeValue = (
                        document.getElementById(
                            'swal-typification-type'
                        ) as HTMLSelectElement
                    )?.value;

                    if (!typificationTypeValue) {
                        Swal.showValidationMessage(
                            'Debes seleccionar el tipo de tipificación'
                        );
                        return;
                    }

                    return {
                        typificationType: typificationTypeValue,
                    };
                },
            });

            if (!modalResult.isConfirmed || !modalResult.value) {
                return;
            }

            const { typificationType: selectedTypificationType } =
                modalResult.value;

            if (!selectedTypificationType) {
                SwalCustomClass.fire({
                    text: 'Debes seleccionar el tipo de tipificación',
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
                return;
            }

            setExportingTemplate(true);
            const body: any = await asyncGetTemplateIvrTypificationBase({
                typificationType:
                    selectedTypificationType as IvrTypificationTemplateTypeEnum,
            });
            if (body?.items?.url) {
                const formatLabel =
                    TYPIFICATION_TYPE_OPTIONS.find(
                        (o) => o.id === selectedTypificationType
                    )?.name || String(selectedTypificationType);

                await downloadTemplateWithFormatName(
                    body.items.url,
                    formatLabel
                );
                SwalCustomClass.fire({
                    text: messageOkTemplate,
                    icon: 'success',
                    iconColor: '#1d1d1d',
                });
                return;
            }
            SwalCustomClass.fire({
                text: messageErrorTemplate,
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } catch (error) {
            SwalCustomClass.fire({
                text: messageErrorTemplate,
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } finally {
            setExportingTemplate(false);
        }
    };

    const handleSyncToCrmware = async () => {
        const result = await Swal.fire({
            title: syncConfirmTitle,
            text: syncConfirmMessage,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1d1d1d',
        });

        if (!result.isConfirmed) {
            return;
        }

        setSyncingCrmware(true);

        try {
            await asyncSyncIvrTypificationBaseToCrmware();
            SwalCustomClass.fire({
                text: syncSuccessMessage,
                icon: 'success',
                iconColor: '#1d1d1d',
            });
        } catch (err: unknown) {
            const httpErr = err as IvrTypificationSyncHttpError;
            const message =
                typeof httpErr?.body === 'object' && httpErr?.body != null
                    ? (httpErr.body as any)?.message
                    : undefined;

            SwalCustomClass.fire({
                text: message || syncErrorMessage,
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } finally {
            setSyncingCrmware(false);
        }
    };

    const dataActive = [
        { id: 'true', name: 'Si' },
        { id: 'false', name: 'No' },
    ];
    return (
        <>
            <ListView
                title={titleList}
                titleButtonAdd="Importar"
                startIconButtonAdd={<FileUploadIcon color="secondary" />}
                onClickAdd={() => setOpen(true)}
                onClickBack={() => navigate(HOME)}
                idButtonGeneral="ivr-tb-btn-export"
                enabledButtonGeneral={true}
                loadingButtonGeneral={exportingTemplate}
                titleButtonGeneral="Exportar template"
                startIconButtonGeneral={<DownloadIcon color="secondary" />}
                onClickButtonGeneral={() => handleDownloadTemplate()}
                enabledButtonSecondary={true}
                idButtonSecondary="ivr-tb-btn-sync-crm"
                titleButtonSecondary={syncButton}
                startIconButtonSecondary={<SyncIcon color="secondary" />}
                onClickButtonSecondary={() => handleSyncToCrmware()}
                loadingButtonSecondary={syncingCrmware}
                sxButtonSecondary={{
                    mr: {
                        xs: 1,
                        sm: 1,
                    },
                    mt: { xs: 1, sm: 1 },
                }}
                enabledHelpButton={true}
                idHelpButton="ivr-tb-btn-help"
                titleHelpButton={helpTourButton}
                startIconHelpButton={<HelpOutlineIcon color="primary" />}
                onClickHelpButton={() => startIvrTypificationBaseTour()}
                idButtonAdd="ivr-tb-btn-import"
                dataGridContainerId="ivr-tb-grid"
                enabledSecondButtonGeneral={Boolean(
                    initialProcessName ||
                        processCode ||
                        typificationType ||
                        typificationCode ||
                        initialTypification ||
                        subTypificationCode ||
                        initialSubTypificationName ||
                        pqrstType ||
                        customerType ||
                        responseTime ||
                        active !== ''
                )}
                titleSecondButtonGeneral="Limpiar Filtros"
                startIconSecondButtonGeneral={<CancelIcon color="secondary" />}
                onClickSecondButtonGeneral={() => clearFilters()}
                columns={columns}
                rows={rows}
                rowCount={lengthRows}
                disableRow={true}
                loading={loading}
                onSetPage={(paginationModel: any) => paginator(paginationModel)}
                filterAvanced={true}
                onFilterAvanced={(filter: any) => validateFilters(filter)}
                enabledSearch={true}
                searchField={
                    <Box id="ivr-tb-filters" sx={{ width: '100%' }}>
                        <Grid container spacing={{ xs: 1.5 }}>
                            <Grid item xs={12} md={4}>
                                <FieldSelect
                                    data={TYPIFICATION_TYPE_OPTIONS}
                                    label="Tipo tipificación"
                                    name="typificationType"
                                    size="small"
                                    value={typificationType}
                                    onChange={(value: any) => {
                                        setTypificationType(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Proceso inicial"
                                    name="initialProcessName"
                                    size="small"
                                    value={initialProcessName}
                                    onChange={(value: any) => {
                                        setInitialProcessName(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Codigo proceso"
                                    name="processCode"
                                    size="small"
                                    value={processCode}
                                    onChange={(value: any) => {
                                        setProcessCode(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Codigo tipificacion"
                                    name="typificationCode"
                                    size="small"
                                    value={typificationCode}
                                    onChange={(value: any) => {
                                        setTypificationCode(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Tipificacion inicial"
                                    name="initialTypification"
                                    size="small"
                                    value={initialTypification}
                                    onChange={(value: any) => {
                                        setInitialTypification(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Codigo subtipificacion"
                                    name="subTypificationCode"
                                    size="small"
                                    value={subTypificationCode}
                                    onChange={(value: any) => {
                                        setSubTypificationCode(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Subtipificacion inicial"
                                    name="initialSubTypificationName"
                                    size="small"
                                    value={initialSubTypificationName}
                                    onChange={(value: any) => {
                                        setInitialSubTypificationName(
                                            value?.target?.value || ''
                                        );
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Tipo PQRST"
                                    name="pqrstType"
                                    size="small"
                                    value={pqrstType}
                                    onChange={(value: any) => {
                                        setPqrstType(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Tipo cliente"
                                    name="customerType"
                                    size="small"
                                    value={customerType}
                                    onChange={(value: any) => {
                                        setCustomerType(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldText
                                    label="Tiempo respuesta"
                                    name="responseTime"
                                    size="small"
                                    value={responseTime}
                                    onChange={(value: any) => {
                                        setResponseTime(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldSelect
                                    data={dataActive}
                                    label="Activo"
                                    name="active"
                                    size="small"
                                    value={active}
                                    onChange={(value: any) => {
                                        setActive(value?.target?.value || '');
                                        setLoading(true);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                }
            />
            {open && (
                <ModalIvrTypificationBase
                    open={open}
                    onClose={(value: boolean) => setOpen(value)}
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                />
            )}
        </>
    );
};

export default ListIvrTypificationBase;
